import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import FileUpload from '../../../components/ui/FileUpload'
import { DOCUMENT_ACCEPT } from '../../../lib/uploadAccept'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { requiredText } from '../../../lib/forms/rules'
import { parseISODate, todayStart, toISODate } from '../../../lib/datetime'
import { useActionLevels } from '../hooks/useActionLevels'
import ActionPeopleEditor from './ActionPeopleEditor'
import SinglePersonEditor from './SinglePersonEditor'
import TaskClassificationFields from './TaskClassificationFields'
import { COLUMN_BYTES } from '../../../lib/columnBytes'
import {
  ASSIGNEE_TYPE,
  classificationIds,
  EMPTY_CLASSIFICATION,
  minutePersonToInput,
  withAssigneeType,
  personInputToMinutePerson,
  type MinutePerson,
  type PersonInput,
  type TaskClassification,
  type StandaloneActionCreateRequest, IMPORTANCE_LEVEL, URGENCY_LEVEL } from '../types'

const schema = z.object({
  name: requiredText(250, COLUMN_BYTES.meetingMinutesText),
  description: requiredText(2000, COLUMN_BYTES.meetingMinutesText),
})

interface Props {
  visible: boolean
  submitting: boolean
  /** Ekler ikinci bir istekle yüklenir (kayıt id'si gerekir) → payload'dan AYRI parametre. */
  onSubmit: (payload: StandaloneActionCreateRequest, files: File[]) => void
  onHide: () => void
}

/**
 * Toplantıdan <b>bağımsız</b> aksiyon oluştur (analiz 6.5.3 — Aksiyonlar menüsünden). Toplantı/yer ilişkisi yok.
 * Alanlar: Aksiyon Tanımı + Aksiyon Açıklaması (RHF+zod) + Aciliyet/Önem Derecesi (<b>OPSİYONEL</b> — toplantıya
 * bağlı aksiyonda zorunludur, burada boş bırakılabilir) + Sorumlu(lar) (<b>çoklu</b>, en az bir; sicil bazında
 * tekil; HR aramasından) + Termin (zorunlu, geçmiş seçilemez) + <b>Dokümanlar</b> (opsiyonel).
 *
 * <p><b>Dokümanlar burada yalnız TOPLANIR, yüklenmez</b> (kullanıcı isteği 28.09.2026): eklemek için
 * önce kaydın oluşması ve id alması gerekiyordu, yani dosya ancak Görev Detayı'ndan eklenebiliyordu.
 * Seçilen dosyalar `onSubmit`'in ikinci parametresiyle çağırana verilir; asıl yükleme
 * {@code useCreateStandaloneAction} içinde, kayıt oluştuktan hemen sonra yapılır. Aynı kalıbın
 * emsali: {@code ActivityFormPage} (Etkinlik) ve tutanak oluşturma ekranı.
 *
 * <p>Detay ekranındaki bölümün aksine burada <b>indirme/kaldırma tablosu yoktur</b>: dosyalar henüz
 * sunucuya gitmemiştir, kaldırma işi seçicinin kendi listesindedir.
 */
export default function StandaloneActionFormDialog({
  visible,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(schema, {
    defaultValues: { name: '', description: '' },
  })
  // Sorumlu(lar) — çoklu; yerel gösterim için MinutePerson (fullName ile), gönderirken PersonInput'a çevrilir.
  const [mainResponsible, setMainResponsibleState] = useState<MinutePerson | null>(null)
  const [responsibles, setResponsibles] = useState<MinutePerson[]>([])
  const [deadline, setDeadline] = useState('')
  // Aciliyet/Önem Derecesi — bu formda OPSİYONEL (bağımsız görevde zorunlu değil; zod şeması ve
  // sunucudaki StandaloneActionCreateRequest ikisini de zorunlu tutmaz).
  //
  // Varsayılan yine **Normal** (kullanıcı isteği 2026-08-26): en sık seçilen değer, boş başlamak her
  // kayıtta iki fazla tıklama demekti. Zorunluluk gerekçesi DEĞİL — o gerekçe toplantıya bağlı
  // görev satırı için geçerli (bkz. `ActionsEditor.emptyRow`); ilk hâlde buraya yanlışlıkla
  // "ikisi de zorunlu alan" yazılmıştı (push kapısı code review bulgusu).
  // Kullanıcı isterse boşaltabilir — varsayılan bir kısıt değil, kolaylıktır.
  const [urgencyLevelId, setUrgencyLevelId] = useState<number | null>(URGENCY_LEVEL.NORMAL)
  const [importanceLevelId, setImportanceLevelId] = useState<number | null>(IMPORTANCE_LEVEL.NORMAL)
  // Stratejik Hedef / Katkı (127) — opsiyonel, çoklu; seçenekler aciliyet/önemle aynı uçtan gelir.
  const [classification, setClassification] = useState<TaskClassification>(EMPTY_CLASSIFICATION)
  // Oluşturma anında seçilen dokümanlar — sunucuya gitmez, kayıt oluşunca çağıran yükler.
  const [files, setFiles] = useState<File[]>([])
  const [showErrors, setShowErrors] = useState(false)
  // Aynı kişinin iki rolde seçilmesi denendi mi? Hangi bölümde uyarı gösterileceğini tutar (2026-08-12).
  const [roleConflict, setRoleConflict] = useState<'main' | 'secondary' | null>(null)

  // Aciliyet/Önem Derecesi seçenekleri (editörle aynı kaynak) — yalnız diyalog açıkken çekilir; gizli
  // monte edildiği ana sayfada yetkisiz kullanıcıya 403 uyarısı göstermesin.
  const { data: levels } = useActionLevels(visible)

  useEffect(() => {
    if (visible) {
      reset({ name: '', description: '' })
      setMainResponsibleState(null)
      setResponsibles([])
      setDeadline('')
      setUrgencyLevelId(URGENCY_LEVEL.NORMAL)
      setImportanceLevelId(IMPORTANCE_LEVEL.NORMAL)
      setClassification(EMPTY_CLASSIFICATION)
      // Seçilen dosyalar da sıfırlanmalı: aksi hâlde bir sonraki "Görev Oluştur"da önceki kaydın
      // ekleri hazır gelir ve yanlış göreve iliştirilir.
      setFiles([])
      setShowErrors(false)
      setRoleConflict(null)
    }
  }, [visible, reset])

  // Termin geçmiş olamaz → alt sınır bugün.
  const minDeadline = todayStart()

  // Sorumlu ekle/çıkar (sicil bazında tekil — editör zaten mükerrer eklemeyi engeller).
  // Ana Sorumlu olarak seçili kişi Yan olarak EKLENEMEZ ve seçim sessizce taşınmaz: kullanıcı kuralı
  // 2026-08-12 → çakışma hata olarak gösterilir, kişi önce diğer rolden çıkarılır. (Takip edenler bu
  // kuralın dışındadır: takipçi olmak Ana/Yan Sorumlu seçilmeye engel değildir.)
  const onAddResponsible = (p: PersonInput) => {
    if (mainResponsible?.employeeId === p.employeeId) {
      setRoleConflict('secondary')
      return
    }
    setRoleConflict(null)
    setResponsibles((prev) =>
      prev.some((x) => x.employeeId === p.employeeId)
        ? prev
        : [...prev, personInputToMinutePerson(p)],
    )
  }
  /** Ana Sorumlu seç — kişi Yan listesindeyse reddedilir (yukarıdaki kuralın simetriği). */
  const onSetMainResponsible = (p: PersonInput) => {
    if (responsibles.some((x) => x.employeeId === p.employeeId)) {
      setRoleConflict('main')
      return
    }
    setRoleConflict(null)
    setMainResponsibleState(personInputToMinutePerson(p))
  }
  const onRemoveResponsible = (employeeId: string) => {
    // Çakışma uyarısının çözümü "kişiyi diğer rolden çıkar"dır → çıkarınca uyarı da kalkmalı.
    setRoleConflict(null)
    setResponsibles((prev) => prev.filter((x) => x.employeeId !== employeeId))
  }

  const onValid = (v: { name: string; description: string }) => {
    // Zorunlu: Sorumlu + Termin. Aciliyet/Önem Derecesi opsiyoneldir.
    // Ana Sorumlu (2026-08-12) zorunlu; Yan Sorumlu opsiyoneldir.
    if (mainResponsible == null || deadline === '') {
      setShowErrors(true)
      return
    }
    onSubmit(
      {
        name: v.name,
        description: v.description,
        deadline,
        // Tek listede gider; roller alan üzerinden ayrışır (backend Ana'yı tekil doğrular).
        responsibles: [
          minutePersonToInput(withAssigneeType(mainResponsible, ASSIGNEE_TYPE.MAIN)),
          ...responsibles.map((r) =>
            minutePersonToInput(withAssigneeType(r, ASSIGNEE_TYPE.SECONDARY)),
          ),
        ],
        // Aciliyet/Önem Derecesi opsiyonel → seçilmişse gönder, değilse null.
        urgencyLevelId,
        importanceLevelId,
        ...classificationIds(classification),
      },
      files,
    )
  }

  return (
    <FormDialog
      visible={visible}
      header={t('actions.create.title')}
      submitLabel={t('actions.create.save')}
      submitting={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
      width="42rem"
    >
      <div className="admin-form">
        {/* Kayıt grubu (122) formda YOK (analist, 19.10.2026): sunucu oluşturanın Grup Yönetimi üyeliğinden
            türetir — tek gruba üyeyse o grup, değilse Genel Müdür. */}
        <FormTextField
          control={control}
          name="name"
          label={t('actions.create.name')}
          placeholder={t('actions.create.namePlaceholder')}
          required
          maxLength={250}
        />
        <FormTextareaField
          control={control}
          name="description"
          label={t('actions.create.description')}
          placeholder={t('actions.create.descriptionPlaceholder')}
          required
          rows={4}
          maxLength={2000}
        />

        {/* Aciliyet Derecesi · Önem Derecesi (OPSİYONEL — * yok; bağımsız aksiyonda zorunlu değil, boş
            bırakılabilir → showClear ile temizlenebilir). */}
        <div className="act-form-scores">
          <label className="act-field">
            <span className="ui-field-label">{t('actions.form.urgency')}</span>
            <Dropdown
              value={urgencyLevelId}
              options={levels?.urgencyLevels ?? []}
              optionValue="id"
              optionLabel="name"
              showClear
              placeholder={t('actions.form.selectPlaceholder')}
              onChange={(e) => setUrgencyLevelId(e.value ?? null)}
            />
          </label>

          <label className="act-field">
            <span className="ui-field-label">{t('actions.form.importance')}</span>
            <Dropdown
              value={importanceLevelId}
              options={levels?.importanceLevels ?? []}
              optionValue="id"
              optionLabel="name"
              showClear
              placeholder={t('actions.form.selectPlaceholder')}
              onChange={(e) => setImportanceLevelId(e.value ?? null)}
            />
          </label>
        </div>

        {/* Ana Sorumlu — TEKİL ve zorunlu; seçici açık kalır, yeni kişi eskisinin yerini alır. */}
        <div className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.detail.mainResponsible.title')} <span className="ui-req">*</span>
          </span>
          <SinglePersonEditor
            person={mainResponsible}
            editable
            canClear={false}
            onSet={onSetMainResponsible}
            onClear={() => {}}
            placeholder={t('actions.create.personPlaceholder')}
            removeLabel={t('actions.create.removePerson')}
          />
          {showErrors && mainResponsible == null && (
            <span className="act-form-err">{t('actions.detail.mainResponsible.required')}</span>
          )}
          {/* Yan Sorumlu'yu Ana yapmaya çalışmak: seçim uygulanmaz, gerekçesi burada söylenir. */}
          {roleConflict === 'main' && (
            <span className="act-form-err">{t('actions.detail.roleConflict.mainIsSecondary')}</span>
          )}
        </div>

        {/* Yan Sorumlu — çoklu ve opsiyonel; her biri için otomatik alt aksiyon üretilir. */}
        <div className="act-field act-field--full">
          <span className="ui-field-label">{t('actions.detail.secondaryResponsibles.title')}</span>
          <ActionPeopleEditor
            people={responsibles}
            editable
            onAdd={onAddResponsible}
            onRemove={onRemoveResponsible}
            placeholder={t('actions.create.personPlaceholder')}
            removeLabel={t('actions.create.removePerson')}
          />
          {roleConflict === 'secondary' && (
            <span className="act-form-err">{t('actions.detail.roleConflict.secondaryIsMain')}</span>
          )}
        </div>

        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.create.deadline')} <span className="ui-req">*</span>
          </span>
          <Calendar
            value={parseISODate(deadline)}
            minDate={minDeadline}
            dateFormat="dd.mm.yy"
            showIcon
            className={showErrors && deadline === '' ? 'p-invalid' : undefined}
            onChange={(e) => setDeadline(toISODate(e.value ?? null))}
          />
          {showErrors && deadline === '' && (
            <span className="act-form-err">{t('actions.create.required')}</span>
          )}
        </label>

        {/* Stratejik Hedef · Katkı (127) — OPSİYONEL, çoklu; talep gereği Dokümanlar'ın hemen üstünde. */}
        <TaskClassificationFields options={levels} value={classification} onChange={setClassification} />

        {/* Dokümanlar — OPSİYONEL. Detay ekranındaki bölümle aynı metinler ve aynı kabul listesi
            (DOCUMENT_ACCEPT) kullanılır; iki yüzeyin ayrışmaması için anahtarlar çoğaltılmadı. */}
        <div className="act-field act-field--full">
          <span className="ui-field-label">{t('actions.detail.documents.title')}</span>
          <FileUpload
            files={files}
            onChange={setFiles}
            accept={DOCUMENT_ACCEPT}
            label={t('actions.detail.documents.add')}
            hint={t('actions.detail.documents.hint')}
          />
        </div>
      </div>
    </FormDialog>
  )
}
